import React, { useEffect, useRef, useState } from 'react'

import {
  TagComponent,
  Icon,
  Verified,
  Tooltip,
  Progress,
  ChevronDown,
  ChevronUp,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool,
  shape,
  oneOfType,
  number,
  string,
} from 'prop-types'

import { useIs3dPrintModel } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import i18n from 'Pods/Shared/I18n'

import GeometryDetailsSection from './Components/GeometryDetailsSection'
import {
  get3DPrintVerification,
  getFileVerification,
  getSceneVerification,
  getGeometryVerification,
  getTextureVerification,
  getUVVerification,
  getTooltipContent,
} from './Utils'
import DetailsAreaSmallTag from '../DetailsAreaSmallTag/DetailsAreaSmallTag'

function VerifiedByCGTraderSection({ data, geometryDetails, locale = 'en' }) {
  const is3dPrintModel = useIs3dPrintModel()

  const tooltipContent = (
    <span>
      Verified models are of higher quality as they have<br/>
      passed CGT Standard technical and visual checks,<br/>
      making them more professional-grade 3D assets.<br/>
      <a
        href="https://help.cgtrader.com/hc/en-us/sections/12602641776913-PBR-model-standard"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn more.
      </a>
    </span>
  )

  const fbxTooltipContent = i18n.t('itemPage.verifiedByCGTrader.fbxTooltipContent', { ns: 'rails', lng: locale })

  const stlTooltipContent = i18n.t('itemPage.verifiedByCGTrader.stlTooltipContent', { ns: 'rails', lng: locale })

  const geometryTooltipContent =
    i18n.t('itemPage.verifiedByCGTrader.geometryTooltipContent', { ns: 'rails', lng: locale })

  const [showSmallerChecks, setShowSmallerChecks] = useState(false)
  const [shouldCollapseContent, setShouldCollapseContent] = useState(false)
  const [isContentCollapsed, setIsContentCollapsed] = useState(false)
  const hasUserToggled = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const evaluateShouldCollapse = () => {
      setShouldCollapseContent(window.innerWidth <= 960)
    }

    evaluateShouldCollapse()
    window.addEventListener('resize', evaluateShouldCollapse)

    return () => {
      window.removeEventListener('resize', evaluateShouldCollapse)
    }
  }, [])

  useEffect(() => {
    if (shouldCollapseContent) {
      if (!hasUserToggled.current) {
        setIsContentCollapsed(true)
      }
    } else {
      hasUserToggled.current = false
      setIsContentCollapsed(false)
    }
  }, [shouldCollapseContent])

  const toggleShowSmallerChecks = () => {
    setShowSmallerChecks((previousValue) => !previousValue)
  }

  const handleToggleContent = () => {
    hasUserToggled.current = true
    setIsContentCollapsed((previousValue) => !previousValue)
  }

  const handleCleanGeometryKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggleShowSmallerChecks()
    }
  }

  const contentId = is3dPrintModel
    ? 'verified-by-cgtrader-3d-print-content'
    : 'verified-by-cgtrader-content'

  const sectionContentClassName = `details-area__section-content${
    isContentCollapsed ? ' details-area__section-content--collapsed' : ''
  }`

  const renderSectionHeader = () => (
    <div
      className={classnames('details-area__section-header',
      shouldCollapseContent && 'details-area__section-header--collapsible',
      'details-area__section-header--verified-by-cgtrader')}
    >
      <div className="details-area__section-header--verified-by-cgtrader-tooltip">
        <Tooltip
          content={tooltipContent}
          id="verified-by-cgtrader"
          delayHide={500}
          clickable
          tooltipClassName="details-area__tooltip-content"
          place="right"
        >
          <span className="details-area__section-title">Verified by CGTrader</span>
        </Tooltip>
      </div>
      {shouldCollapseContent && (
        <button
          type="button"
          className="details-area__section-collapse-button"
          onClick={handleToggleContent}
          aria-expanded={!isContentCollapsed}
          aria-controls={contentId}
        >
          CGTrader Quality Checks
          <Icon svgComponent={isContentCollapsed ? ChevronDown : ChevronUp} />
        </button>
      )}
    </div>
  )

  if (is3dPrintModel) {
    return (
      <div className="details-area__section details-area__section--provided-by-cgtrader">
        {renderSectionHeader()}
        <div className={sectionContentClassName} id={contentId}>
          <Tooltip
            content={stlTooltipContent}
            id="verified-by-cgtrader-stl"
            place="right"
            tooltipClassName="details-area__tooltip-content"
          >
            <TagComponent
              type="ghost"
              trailIcon={<Icon svgComponent={Verified} className="details-area__verified-icon" />}
              className="details-area__tag"
            >
              STL
            </TagComponent>
          </Tooltip>

          <div
            className="details-area__section details-area__section--toggle"
            onClick={toggleShowSmallerChecks}
            onKeyDown={handleCleanGeometryKeyDown}
            role="button"
            tabIndex={0}
            aria-expanded={showSmallerChecks}
          >
            <Tooltip
              content={geometryTooltipContent}
              id="verified-by-cgtrader-geometry"
              place="right"
            >
              <span className="details-area__section-subtitle">Geometry</span>
            </Tooltip>
            <div className="details-area__progress-container-wrapper">
              <div className="details-area__progress-container">
                <Progress
                  height={12}
                  isSpaced={false}
                  progress={data.score}
                  useGradient
                  className="details-area__progress"
                />
              </div>
              <span className="details-area__section-subtitle">{data.score}%</span>
            </div>
          </div>

          {showSmallerChecks && (
            <div className="details-area__geometry-details">
              <DetailsAreaSmallTag
                title="Watertight geometry"
                active={get3DPrintVerification(data, 'noOpenEdges')}
                tooltip={getTooltipContent('3dprint', 'noOpenEdges')}
              />
              <DetailsAreaSmallTag
                title="Manifold geometry"
                active={get3DPrintVerification(data, 'manifoldGeometry')}
                tooltip={getTooltipContent('3dprint', 'manifoldGeometry')}
              />
              <DetailsAreaSmallTag
                title="No self-intersections"
                active={get3DPrintVerification(data, 'noSelfIntersections')}
                tooltip={getTooltipContent('3dprint', 'noSelfIntersections', locale)}
              />
              <DetailsAreaSmallTag
                title="No overlapping faces"
                active={get3DPrintVerification(data, 'noOverlappingFaces')}
                tooltip={getTooltipContent('3dprint', 'noOverlappingFaces')}
              />
              <DetailsAreaSmallTag
                title="No zero angled corners"
                active={get3DPrintVerification(data, 'noZeroAngledCorners')}
                tooltip={getTooltipContent('3dprint', 'noZeroAngledCorners')}
              />
              <DetailsAreaSmallTag
                title="No zero-length edges"
                active={get3DPrintVerification(data, 'noZeroLengthEdges')}
                tooltip={getTooltipContent('3dprint', 'noZeroLengthEdges')}
              />
              <DetailsAreaSmallTag
                title="No zero-area faces"
                active={get3DPrintVerification(data, 'noZeroAreaFaces')}
                tooltip={getTooltipContent('3dprint', 'noZeroAreaFaces')}
              />
              <DetailsAreaSmallTag
                title="No bad continuous edges"
                active={get3DPrintVerification(data, 'noBadContiguousEdges')}
                tooltip={getTooltipContent('3dprint', 'noBadContiguousEdges')}
              />
            </div>
          )}

          {geometryDetails && <GeometryDetailsSection geometryDetails={geometryDetails} locale={locale} />}
        </div>
      </div>
    )
  }

  return (
    <div className="details-area__section details-area__section--provided-by-cgtrader">
      {renderSectionHeader()}
      <div className={sectionContentClassName} id={contentId}>
        <Tooltip
            content={fbxTooltipContent}
            id="verified-by-cgtrader-fbx"
            place="right"
            tooltipClassName="details-area__tooltip-content"
          >
            <TagComponent
              type="ghost"
              trailIcon={<Icon svgComponent={Verified} className="details-area__verified-icon" />}
              className="details-area__tag"
            >
              FBX
            </TagComponent>
          </Tooltip>
        <div className="details-area__section">
          <span className="details-area__section-subtitle">File & scene</span>
          <div className="details-area__file-details">
            <DetailsAreaSmallTag
              title="Binary FBX"
              active={getFileVerification(data, 'fbxFormat')}
              tooltip={getTooltipContent('file', 'fbxFormat')}
              place="right"
            />
            <DetailsAreaSmallTag
              title="No unsupported objects"
              active={getSceneVerification(data, 'dccObjectTypes')}
              tooltip={getTooltipContent('scene', 'dccObjectTypes')}
            />
          </div>
        </div>
        <div className="details-area__section">
          <span className="details-area__section-subtitle">Geometry</span>
          <div className="details-area__geometry-details">
            <DetailsAreaSmallTag
              title="No N-gons"
              active={getGeometryVerification(data, 'noNgons')}
              tooltip={getTooltipContent('geometry', 'noNgons')}
              place="right"
            />
            <DetailsAreaSmallTag
              title="No faceted geometry"
              active={getGeometryVerification(data, 'facetedGeometry')}
              tooltip={getTooltipContent('geometry', 'facetedGeometry')}
            />
            <DetailsAreaSmallTag
              title="Manifold geometry"
              active={getGeometryVerification(data, 'nonManifoldVertices')}
              tooltip={getTooltipContent('geometry', 'nonManifoldVertices')}
            />
          </div>
        </div>
        <div className="details-area__section">
          <span className="details-area__section-subtitle">Textures & material</span>
          <div className="details-area__texture-details">
            <DetailsAreaSmallTag
              title="PBR textures"
              active={getTextureVerification(data, 'missingRequiredTextures')}
              tooltip={getTooltipContent('texture', 'missingRequiredTextures')}
              place="right"
            />
            <DetailsAreaSmallTag
              title="No embed textures"
              active={getTextureVerification(data, 'embeddedTexturesFbx')}
              tooltip={getTooltipContent('texture', 'embeddedTexturesFbx')}
            />
            <DetailsAreaSmallTag
              title="Square textures"
              active={getTextureVerification(data, 'textureAspectRatio')}
              tooltip={getTooltipContent('texture', 'textureAspectRatio')}
            />
            <DetailsAreaSmallTag
              title="Power of 2 texture sizes"
              active={getTextureVerification(data, 'textureResolutionNotPowerOf2')}
              tooltip={getTooltipContent('texture', 'textureResolutionNotPowerOf2')}
              place="right"
            />
            <DetailsAreaSmallTag
              title="Assigned materials"
              active={getTextureVerification(data, 'numberOfMaterialsPerObject')}
              tooltip={getTooltipContent('texture', 'numberOfMaterialsPerObject')}
            />
          </div>
        </div>
        <div className="details-area__section">
          <span className="details-area__section-subtitle">UVs & naming</span>
          <div className="details-area__uv-details">
            <DetailsAreaSmallTag
              title="No UV overlaps"
              active={getUVVerification(data, 'overlappedUvPerUVIsland')}
              tooltip={getTooltipContent('uv', 'overlappedUvPerUVIsland')}
              place="right"
            />
            <DetailsAreaSmallTag
              title="UV unwrapped model"
              active={getUVVerification(data, 'numberOfUVs')}
              tooltip={getTooltipContent('uv', 'numberOfUVs')}
            />
            <DetailsAreaSmallTag
              title="Allowed characters"
              active={getUVVerification(data, 'allowedCharacters')}
              tooltip={getTooltipContent('naming', 'allowedCharacters')}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

VerifiedByCGTraderSection.propTypes = {
  data: oneOfType([
    shape({
      files: shape({
        fbxFormat: shape({
          pass: bool,
        }),
      }),
      geometry: shape({
        facetedGeometry: shape({
          pass: bool,
        }),
        noNgons: shape({
          pass: bool,
        }),
        nonManifoldVertices: shape({
          pass: bool,
        }),
      }),
      naming: shape({
        allowedCharacters: shape({
          pass: bool,
        }),
      }),
      scene: shape({
        dccObjectTypes: shape({
          pass: bool,
        }),
      }),
      texturesAndMaterials: shape({
        embeddedTextures: shape({
          pass: bool,
        }),
        missingRequiredTextures: shape({
          pass: bool,
        }),
        numberOfMaterialsPerObject: shape({
          pass: bool,
        }),
        textureAspectRatio: shape({
          pass: bool,
        }),
        textureResolutionNotPowerOf2: shape({
          pass: bool,
        }),
      }),
      uV: shape({
        numberOfUVs: shape({
          pass: bool,
        }),
        overlappedUvPerUVIsland: shape({
          pass: bool,
        }),
      }),
    }),
    shape({
      cleanGeometry: shape({
        manifoldGeometry: bool.isRequired,
        noSelfIntersections: bool.isRequired,
        noOverlappingFaces: bool.isRequired,
        noZeroAngledCorners: bool.isRequired,
        noZeroLengthEdges: bool.isRequired,
        noZeroAreaFaces: bool.isRequired,
        noBadContiguousEdges: bool.isRequired,
        noOpenEdges: bool.isRequired,
      }),
      score: number.isRequired,
    }),
  ]),
  geometryDetails: shape({
    boundingBoxX: number,
    boundingBoxY: number,
    boundingBoxZ: number,
    minimalAllowedThickness: number,
    tooThinWallsCount: number,
    volume: number,
  }),
  locale: string,
}

export default VerifiedByCGTraderSection
